import { cx } from '@emotion/css';
import { Field } from 'formik';
import PropTypes from 'prop-types';
import React, { useCallback } from 'react';

import InvalidMessage from 'App/common/styles/InvalidMessage';
import FieldLabel from './FieldLabel';

function isEvent(input) {
    return !!input?.nativeEvent && input.nativeEvent instanceof Event;
}

const FormikFieldInner = ({
    formikProps: { field, form, meta },

    WrappedComponent,
    suppressFieldLabel,
    isFieldset,

    onChange,
    label,
    required,
    suppressAsterisk,
    preInputContent,
    className,
    ...rest
}) => {
    const { value, onBlur } = field;
    const hasError = (meta.touched || form.submitCount > 0) && !!meta.error;

    const handleChange = useCallback(
        (...args) => {
            // Custom onChange supplied from component
            if (onChange) {
                return onChange(...args);
            }
            if (isEvent(args[0])) {
                return field.onChange(...args);
            }
            return form.setFieldValue(field.name, args[0]);
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps -- need to ignore form and field as they are always the same
        [onChange]
    );

    const innerContent = (
        <>
            {preInputContent}
            <WrappedComponent
                name={field.name}
                value={value}
                onBlur={onBlur}
                onChange={handleChange}
                label={suppressFieldLabel ? label : undefined}
                className={cx(className, {
                    'field-required': suppressFieldLabel && required,
                    'has-error': suppressFieldLabel && hasError,
                })}
                // `rest` at the bottom, should override if a specific component supplies specific props
                {...rest}
            />
            {hasError ? <InvalidMessage>{meta.error}</InvalidMessage> : null}
        </>
    );

    if (label && !suppressFieldLabel) {
        return (
            <FieldLabel
                fieldName={field.name}
                label={label}
                required={required}
                suppressAsterisk={suppressAsterisk}
                hasError={hasError}
                isFieldset={isFieldset}
            >
                {innerContent}
            </FieldLabel>
        );
    }

    return innerContent;
};
FormikFieldInner.propTypes = {
    formikProps: PropTypes.shape({
        field: PropTypes.shape({
            name: PropTypes.string.isRequired,
            // eslint-disable-next-line react/forbid-prop-types -- made to be generic to any values
            value: PropTypes.any,
            onChange: PropTypes.func.isRequired,
            onBlur: PropTypes.func.isRequired,
        }).isRequired,
        form: PropTypes.shape({
            setFieldValue: PropTypes.func.isRequired,
            submitCount: PropTypes.number.isRequired,
        }).isRequired,
        meta: PropTypes.shape({
            touched: PropTypes.bool.isRequired,
            /**
             * This can be any shape
             */
            error: PropTypes.oneOfType([PropTypes.shape(), PropTypes.string, PropTypes.bool]),
        }).isRequired,
    }).isRequired,

    WrappedComponent: PropTypes.oneOfType([PropTypes.func, PropTypes.shape({})]).isRequired,
    suppressFieldLabel: PropTypes.bool,
    isFieldset: PropTypes.bool,

    onChange: PropTypes.func,
    label: PropTypes.node,
    required: PropTypes.bool.isRequired,
    suppressAsterisk: PropTypes.bool,
    preInputContent: PropTypes.node,
    className: PropTypes.string,
};
FormikFieldInner.defaultProps = {
    suppressFieldLabel: false,
    isFieldset: false,
    onChange: null,
    label: null,
    suppressAsterisk: false,
    preInputContent: null,
    className: '',
};

function asFormikField({ WrappedComponent, suppressFieldLabel, onValidateField, isFieldset }) {
    const FieldComponent = (fieldComponentProps) => {
        const { fieldName, required, requiredErrorText, onValidate, ...rest } = fieldComponentProps;
        const handleValidate = (value) => {
            // run custom validate function if its provided
            if (onValidate) {
                const customValidation = onValidate(value);
                if (customValidation) {
                    return customValidation;
                }
            }

            if (onValidateField) {
                const fieldValidation = onValidateField(value, fieldComponentProps);
                if (fieldValidation) {
                    return fieldValidation;
                }
            }

            if (
                required &&
                (value === undefined || value === null || value === '' || value.length === 0 || value === false)
            ) {
                // generic error message as a fallback if a custom function is not used above
                return requiredErrorText || 'Field is required';
            }
        };

        return (
            <Field name={fieldName} validate={handleValidate}>
                {(formikProps) => (
                    <FormikFieldInner
                        formikProps={formikProps}
                        WrappedComponent={WrappedComponent}
                        suppressFieldLabel={suppressFieldLabel}
                        isFieldset={isFieldset}
                        required={required}
                        {...rest}
                    />
                )}
            </Field>
        );
    };

    FieldComponent.displayName = `asFormikField(${WrappedComponent.displayName || WrappedComponent.name})`;

    FieldComponent.propTypes = {
        fieldName: PropTypes.string.isRequired,
        onChange: PropTypes.func,
        label: PropTypes.node,
        required: PropTypes.bool,
        requiredErrorText: PropTypes.string,
        suppressAsterisk: PropTypes.bool,
        onValidate: PropTypes.func,
        preInputContent: PropTypes.node,
    };

    FieldComponent.defaultProps = {
        onChange: null,
        label: null,
        required: false,
        requiredErrorText: '',
        suppressAsterisk: false,
        onValidate: null,
        preInputContent: null,
    };

    return FieldComponent;
}

export default asFormikField;
